import { supabase } from '@/integrations/supabase/client';

export interface Campaign {
  id: string;
  company_id: string;
  name: string;
  description: string | null;
  template_id: string | null;
  sender_config: any;
  subject: string;
  html_content: string;
  text_content: string | null;
  recipient_filters: any;
  status: string;
  total_recipients: number;
  sent_count: number;
  delivered_count: number;
  opened_count: number;
  clicked_count: number;
  bounced_count: number;
  failed_count: number;
  scheduled_for: string | null;
  started_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export type CampaignInsert = Omit<Campaign, 'id' | 'created_at' | 'updated_at' | 'total_recipients' | 'sent_count' | 'delivered_count' | 'opened_count' | 'clicked_count' | 'bounced_count' | 'failed_count' | 'started_at' | 'completed_at' | 'cancelled_at'>;

export async function fetchCampaigns(companyId: string): Promise<Campaign[]> {
  const { data, error } = await supabase
    .from('email_campaigns')
    .select('*')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as unknown as Campaign[];
}

export async function createCampaign(campaign: Partial<CampaignInsert>): Promise<Campaign> {
  const { data, error } = await supabase
    .from('email_campaigns')
    .insert(campaign as any)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as Campaign;
}

export async function updateCampaign(id: string, updates: Partial<Campaign>): Promise<Campaign> {
  const { data, error } = await supabase
    .from('email_campaigns')
    .update(updates as any)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as Campaign;
}

export async function deleteCampaign(id: string): Promise<void> {
  const { error } = await supabase
    .from('email_campaigns')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

export async function duplicateCampaign(id: string): Promise<Campaign> {
  const { data: original, error: fetchErr } = await supabase
    .from('email_campaigns')
    .select('*')
    .eq('id', id)
    .single();
  if (fetchErr || !original) throw fetchErr || new Error('Campaign not found');

  const copy = original as any;
  delete copy.id;
  delete copy.created_at;
  delete copy.updated_at;
  copy.name = `${copy.name} (Copy)`;
  copy.status = 'draft';
  copy.total_recipients = 0;
  copy.sent_count = 0;
  copy.delivered_count = 0;
  copy.opened_count = 0;
  copy.clicked_count = 0;
  copy.bounced_count = 0;
  copy.failed_count = 0;
  copy.started_at = null;
  copy.completed_at = null;
  copy.cancelled_at = null;
  copy.scheduled_for = null;

  const newCampaign = await createCampaign(copy);

  // Copy email queue entries from the original campaign
  const { data: queueItems } = await supabase
    .from('email_queue')
    .select('*')
    .eq('campaign_id', id);

  if (queueItems && queueItems.length > 0) {
    const clonedItems = queueItems.map((item: any) => {
      const { id, ...rest } = item;
      return {
        ...rest,
        campaign_id: newCampaign.id,
        status: 'pending',
        attempts: 0,
        sent_at: null,
        last_attempt_at: null,
        failed_at: null,
        error_message: null,
        verification_status: null,
        verification_notes: null,
        created_at: new Date().toISOString(),
        scheduled_for: new Date().toISOString(),
      };
    });

    await supabase.from('email_queue').insert(clonedItems as any);

    await supabase
      .from('email_campaigns')
      .update({ total_recipients: clonedItems.length, recipient_filters: { target: 'from_queue' } } as any)
      .eq('id', newCampaign.id);
  }

  return newCampaign;
}
